<script lang="ts">
  import { nf } from '../lib/format';
  import { gradeText, interpret } from '../lib/interpret';
  import { t } from '../lib/i18n';
  import GradeBadge from './GradeBadge.svelte';

  let {
    down,
    up,
    latency,
    jitter,
    loadedDown,
    loadedUp,
    grade,
    loss = null,
    serverName = '',
    warnings = [],
  }: {
    down: number | null | undefined;
    up: number | null | undefined;
    latency: number | null | undefined;
    jitter: number | null | undefined;
    loadedDown: number | null | undefined;
    loadedUp: number | null | undefined;
    grade: string | null | undefined;
    loss?: number | null;
    serverName?: string;
    warnings?: string[];
  } = $props();
  const usages = $derived(interpret({ down, up, latency, jitter, grade }));
</script>

<section class="card" aria-labelledby="res-titre">
  <h2 id="res-titre">Résultat {#if serverName}<span class="muted small">— {t('res.server')} {serverName}</span>{/if}</h2>
  <dl class="metrics">
    <div><dt>{t('res.download')}</dt><dd><strong style="color: var(--down)">{nf(down)}</strong> {t('unit.mbps')}</dd></div>
    <div><dt>{t('res.upload')}</dt><dd><strong style="color: var(--up)">{nf(up)}</strong> {t('unit.mbps')}<br /><span class="small muted">{t('res.uploadNote')}</span></dd></div>
    <div><dt>{t('res.latency')}</dt><dd><strong>{nf(latency, 1)}</strong> ms <span class="small muted">({t('res.jitter')} {nf(jitter, 1)} ms)</span></dd></div>
    <div>
      <dt>{t('res.loaded')}</dt>
      <dd><strong>{nf(Math.max(loadedDown ?? 0, loadedUp ?? 0) || null, 0)}</strong> ms <span class="small muted">(↓ {nf(loadedDown, 0)} · ↑ {nf(loadedUp, 0)})</span></dd>
    </div>
    <div class="grade"><dt>{t('res.grade')}</dt><dd><span class="badge-wrap"><GradeBadge {grade} /></span> <span class="small">{gradeText[grade ?? '?']}</span></dd></div>
    {#if loss !== null && loss !== undefined}
      <div><dt>{t('res.loss')}</dt><dd><strong>{nf(loss, 1)}</strong> %</dd></div>
    {/if}
  </dl>
  {#each warnings as w}
    <p class="notice" role="note">{w}</p>
  {/each}
  <h3>Ce que permet votre connexion</h3>
  <ul class="usages">
    {#each usages as u}
      <li>
        <span class="mark" class:yes={u.ok} aria-hidden="true">{u.ok ? '✓' : '✕'}</span>
        <span><strong>{u.label}</strong><span class="sr-only">{u.ok ? ' : oui' : ' : limité'}</span> — {u.detail}</span>
      </li>
    {/each}
  </ul>
</section>

<style>
  .metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 12px; margin: 0; }
  .metrics div { animation: pop 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  .metrics div:nth-child(2) { animation-delay: 0.06s; }
  .metrics div:nth-child(3) { animation-delay: 0.12s; }
  .metrics div:nth-child(4) { animation-delay: 0.18s; }
  .metrics div:nth-child(n + 5) { animation-delay: 0.24s; }
  .usages li { animation: pop 0.4s ease both; }
  .usages li:nth-child(2) { animation-delay: 0.05s; }
  .usages li:nth-child(3) { animation-delay: 0.1s; }
  .usages li:nth-child(4) { animation-delay: 0.15s; }
  .usages li:nth-child(5) { animation-delay: 0.2s; }
  @keyframes pop { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
  .metrics div {
    position: relative;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 0.85rem 1rem;
    overflow: hidden;
  }
  .metrics div::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--grad);
    opacity: 0.6;
  }
  dt { font-size: 0.74rem; color: var(--muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; }
  dd { margin: 0.25rem 0 0; font-size: 1rem; }
  dd strong { font-family: var(--font-display); font-size: 2rem; font-weight: 700; letter-spacing: -0.03em; font-variant-numeric: tabular-nums; }
  .grade { grid-column: span 2; }
  @media (max-width: 520px) { .grade { grid-column: auto; } }
  .grade dd { display: flex; gap: .6rem; align-items: center; }
  .badge-wrap { flex: none; }
  .usages { list-style: none; padding: 0; margin: 0; display: grid; gap: .45rem; }
  .usages li { display: flex; gap: .6rem; align-items: baseline; }
  .mark { display: inline-grid; place-items: center; width: 1.4rem; height: 1.4rem; border-radius: 50%; font-size: 0.8rem; color: var(--bad); background: color-mix(in oklab, var(--bad) 14%, transparent); font-weight: 900; flex: none; }
  .mark.yes { background: color-mix(in oklab, var(--ok) 16%, transparent); box-shadow: 0 0 12px -2px var(--ok); }
  .mark.yes { color: var(--ok); }
</style>
